// richTextImageUtils.js

export const MIN_HELP_IMAGE_WIDTH = 80;

export const IMAGE_RESIZE_HANDLES = ['nw', 'ne', 'sw', 'se'];

/**
 * parseHelpImageWidth
 *
 * Reads a stored or inline image width into a rounded pixel value.
 *
 * @param {*} value - Raw width from attrs, HTML, or style.
 * @returns {number|null} Rounded pixel width, or null when unset.
 */
export const parseHelpImageWidth = (value) => {
    if (value == null || value === '') return null;

    const numeric = typeof value === 'number'
        ? value
        : parseFloat(String(value).replace(/px$/i, '').trim());

    if (!Number.isFinite(numeric) || numeric <= 0) return null;

    return Math.round(numeric);
};

/**
 * clampHelpImageWidth
 *
 * Keeps a width between the minimum handle size and the article column.
 *
 * @param {*} width - Candidate width.
 * @param {*} maxWidth - Column or measured max width.
 * @returns {number|null} Clamped width, or null when the candidate is invalid.
 */
export const clampHelpImageWidth = (width, maxWidth) => {
    const parsed = parseHelpImageWidth(width);
    if (parsed == null) return null;

    const max = parseHelpImageWidth(maxWidth);
    const upper = max == null ? parsed : Math.max(MIN_HELP_IMAGE_WIDTH, max);

    return Math.min(upper, Math.max(MIN_HELP_IMAGE_WIDTH, parsed));
};

/**
 * nextHelpImageWidth
 *
 * Maps a pointer drag to the next image width, keeping left handles mirrored.
 *
 * @param {object} params - Drag session values.
 * @param {string} params.edge - Corner handle key (`nw`, `ne`, `sw`, `se`).
 * @param {number} params.startX - Pointer X when the drag started.
 * @param {number} params.startWidth - Width when the drag started.
 * @param {number} params.clientX - Current pointer X.
 * @param {number} [params.maxWidth] - Column max width.
 * @returns {number|null} Next width in pixels.
 */
export const nextHelpImageWidth = ({
    edge,
    startX,
    startWidth,
    clientX,
    maxWidth,
}) => {
    const isWest = edge === 'nw' || edge === 'sw' || edge === 'w';
    const delta = isWest ? startX - clientX : clientX - startX;

    return clampHelpImageWidth(startWidth + delta, maxWidth);
};
